import { FC, useState } from 'react';

import { Empty, Skeleton } from 'antd';
import { T } from 'react-translator-component';

import PencilIcon from '../../atoms/icons/Pencil';
import ManualAdjustmentModal from './ManualAdjustmentModal';
import {
  Container,
  DepositContainer,
  DepositDate,
  DepositList,
  DepositType,
  DepositValue,
  DepositsTotalizerValue,
  EditBalanceButton,
  Header,
  SaldoRow,
  SectionTitle,
} from './styled';

import { PaymentTypes } from '../../../types/responses/Transactions';
import { dateTimeFormat } from '../../../utils/dates';
import { hasWalletManualAdjustment } from '../../../utils/hasWalletManualAdjustment/hasWalletManualAdjustment';
import { COLORS } from '../../../utils/theme/colors';

interface DepositsProps {
  user_wallet_payment_pk: number;
  user_pk: number;
  payment_transaction_amounts: number;
  payment_installments: number;
  error: string;
  payment_type: PaymentTypes.WALLET;
  payment_status: string;
  is_idle_payment: boolean;
  pix_qr_code: string;
  created_timestamp: string;
  payment_transaction_amounts_human_readable: string;
  payment_transaction_amount_refunded: number;
  paid_timestamp: string;
  pix_date_of_expiration: string;
  is_paid: boolean;
}

interface DepositsTotalizerProps {
  total: number;
  total_human_readable: string;
}

interface UserWalletProps {
  deposits: DepositsProps[];
  depositsTotalizer: DepositsTotalizerProps | null;
  loading: boolean;
  userPk: number;
  onAdjusted: () => void;
}

export const PAYMENT_TYPES_LABELS = {
  MERCADO_PAGO_CARD: 'Cartão',
  PAGBANK_CARD: 'Cartão',
  MERCADO_PAGO_PIX: 'Pix',
  WALLET: 'Recarga',
  MANUAL_DASHBOARD: 'Manual',
};

const UserWallet: FC<UserWalletProps> = ({
  loading,
  deposits,
  depositsTotalizer,
  userPk,
  onAdjusted,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const canAdjust = hasWalletManualAdjustment();

  const paymentType = (item) => {
    if (item?.is_idle_payment) {
      return 'Ociosidade';
    }
    if (item.payment_status === 'REFUNDED') {
      return 'Reembolso';
    }
    return `${T(PAYMENT_TYPES_LABELS[item?.payment_type])}`;
  };

  const costumerDepositColor = (item) => {
    if (item?.payment_status_detail === 'partially_refunded') {
      return COLORS.popular;
    }

    if (item?.payment_status_detail === 'refunded') {
      return COLORS.stone;
    }

    return item?.payment_transaction_amounts > 0 ? COLORS.popular : COLORS.red;
  };

  const costumerDepositValue = (item) => {
    if (
      item?.payment_status_detail === 'refunded' &&
      item?.payment_transaction_amount_refunded > 0
    ) {
      return `R$ ${item?.payment_transaction_amount_refunded.toFixed(2)}`;
    }

    if (item?.payment_status_detail === 'partially_refunded') {
      const restValueAfterRefund =
        item?.payment_transaction_amounts -
        item?.payment_transaction_amount_refunded;

      if (restValueAfterRefund) {
        return `+ R$ ${restValueAfterRefund.toFixed(2)}`;
      }
    }

    return item?.payment_transaction_amounts_human_readable;
  };

  return (
    <Skeleton
      loading={loading}
      paragraph={{
        rows: 4,
        style: { padding: 16, width: '40%', marginLeft: 64 },
      }}
      title={false}
    >
      <Container>
        <Header>
          <SectionTitle>Extrato Carteira</SectionTitle>

          {!!depositsTotalizer && (
            <SaldoRow>
              <DepositsTotalizerValue
                isNegativeValue={depositsTotalizer.total < 0}
              >{`Saldo: ${depositsTotalizer.total_human_readable}`}</DepositsTotalizerValue>
              {canAdjust && (
                <EditBalanceButton
                  type='button'
                  aria-label={T('Change statement value')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalOpen(true);
                  }}
                >
                  <PencilIcon fill='#333' size={18} />
                </EditBalanceButton>
              )}
            </SaldoRow>
          )}
        </Header>

        {!loading && (!deposits || deposits.length === 0) ? (
          <Empty
            description='Usuário não possui extrato'
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ marginLeft: 64 }}
          />
        ) : (
          <DepositList>
            {deposits?.map((deposit) => {
              const shouldRender =
                deposit?.payment_status !== 'REFUNDED' ||
                (deposit?.payment_status === 'REFUNDED' &&
                  deposit?.payment_transaction_amount_refunded > 0);

              if (shouldRender) {
                return (
                  <DepositContainer
                    key={`${deposit?.user_wallet_payment_pk}-${deposit?.created_timestamp}`}
                  >
                    <DepositDate>{`${dateTimeFormat(
                      deposit?.created_timestamp,
                    )}`}</DepositDate>
                    <DepositType>{paymentType(deposit)}</DepositType>
                    <DepositValue color={costumerDepositColor(deposit)}>
                      {costumerDepositValue(deposit)}
                    </DepositValue>
                  </DepositContainer>
                );
              }
              return null;
            })}
          </DepositList>
        )}

        {canAdjust && (
          <ManualAdjustmentModal
            open={modalOpen}
            userPk={userPk}
            onClose={() => setModalOpen(false)}
            onSuccess={onAdjusted}
          />
        )}
      </Container>
    </Skeleton>
  );
};

export default UserWallet;
